import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Lock } from 'lucide-react'
import {
  ApiError,
  createOpportunity,
  fetchOpportunityAudit,
  fetchCustomers,
  fetchCustomFields,
  fetchForecastDrivers,
  fetchPublicSettings,
  fetchRevenueRules,
  fetchStages,
  markOpportunityLost,
  previewOpportunity,
  updateOpportunity,
} from '@/api/client'
import type {
  CustomFieldDefinition,
  Opportunity,
  OpportunityMonth,
  OpportunityMonthInput,
  OpportunityPreviewResponse,
  RevenuePattern,
} from '@/api/types'
import { useToast } from '@/components/toast-context'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { DateField } from '@/components/DateField'
import { Money } from '@/components/Money'
import { useAuth } from '@/auth/useAuth'
import { isFinanceUser } from '@/api/types'
import { useAppChrome } from '@/context/AppChromeContext'
import { formatDate, formatDateTime } from '@/lib/format-date'

const RAMP_PATTERN = 'ramp_step_up'

interface Props {
  opportunity?: Opportunity | null
  onDirtyChange?: (dirty: boolean) => void
  onConfirmBlockedChange?: (blocked: boolean) => void
  onSaved?: () => void
  formId?: string
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function patternMethod(patterns: RevenuePattern[], code: string): 'headcount' | 'manual_monthly' {
  return patterns.find((p) => p.code === code)?.method ?? 'headcount'
}

function manualMonthsFromInputs(
  inputs: OpportunityMonthInput[] | undefined,
  year: number,
): Record<number, string> {
  const initial: Record<number, string> = {}
  for (const row of inputs ?? []) {
    if (row.year === year && row.amount != null && row.amount !== '') {
      initial[row.month] = String(Math.round(Number(row.amount) || 0))
    }
  }
  return initial
}

function rampMonthsFromInputs(
  inputs: OpportunityMonthInput[] | undefined,
  year: number,
): Record<number, string> {
  const initial: Record<number, string> = {}
  for (const row of inputs ?? []) {
    if (row.year === year && row.headcount != null) {
      initial[row.month] = String(row.headcount)
    }
  }
  return initial
}

function actualMonthsFromOpportunity(
  months: OpportunityMonth[] | undefined,
  year: number,
): Record<number, string> {
  const initial: Record<number, string> = {}
  for (const row of months ?? []) {
    if (row.year !== year) continue
    if (row.closed || row.source === 'actual') {
      initial[row.month] = String(Math.round(Number(row.amount) || 0))
    }
  }
  return initial
}

function buildFormStateFromOpportunity(opportunity: Opportunity | null | undefined, year: number) {
  const monthOverrides: Record<string, string> = {}
  for (const row of opportunity?.months ?? []) {
    if (row.source === 'override') {
      monthOverrides[`${row.year}-${row.month}`] = row.amount
    }
  }
  return {
    customerId: opportunity?.customer_id ?? '',
    name: opportunity?.name ?? '',
    stageId: opportunity?.stage_id ?? '',
    startDate: opportunity?.start_date ?? `${year}-01-01`,
    endDate: opportunity?.end_date ?? `${year}-12-31`,
    billingType: opportunity?.billing_type_code ?? 'fixed_capacity',
    pattern: opportunity?.revenue_pattern_code ?? 'flat_monthly',
    headcount: String(opportunity?.base_headcount ?? 1),
    billRate: opportunity?.bill_rate ?? '0',
    driverCode: opportunity?.forecast_driver_code ?? opportunity?.forecast_driver_id ?? '',
    sowRef: opportunity?.sow_reference ?? '',
    renewsId: opportunity?.renews_opportunity_id ?? '',
    rationale: opportunity?.rationale ?? '',
    manualMonths: manualMonthsFromInputs(opportunity?.month_inputs, year),
    rampMonths: rampMonthsFromInputs(opportunity?.month_inputs, year),
    actualMonths: actualMonthsFromOpportunity(opportunity?.months, year),
    monthOverrides,
    customValues: Object.fromEntries(
      Object.entries(opportunity?.custom_values ?? {}).map(([k, v]) => [k, String(v)]),
    ),
  }
}

export function OpportunityForm({
  opportunity,
  onDirtyChange,
  onConfirmBlockedChange,
  onSaved,
  formId = 'opportunity-form',
}: Props) {
  const { year } = useAppChrome()
  const { user } = useAuth()
  const finance = isFinanceUser(user)
  const toast = useToast()
  const isEdit = Boolean(opportunity?.id)
  const initialForm = useMemo(
    () => buildFormStateFromOpportunity(opportunity, year),
    [opportunity, year],
  )

  const stagesQuery = useQuery({ queryKey: ['config', 'stages'], queryFn: () => fetchStages() })
  const rulesQuery = useQuery({ queryKey: ['config', 'revenue-rules'], queryFn: fetchRevenueRules })
  const customersQuery = useQuery({ queryKey: ['customers'], queryFn: fetchCustomers })
  const driversQuery = useQuery({ queryKey: ['config', 'drivers'], queryFn: fetchForecastDrivers })
  const customFieldsQuery = useQuery({
    queryKey: ['config', 'custom-fields'],
    queryFn: async () => (await fetchCustomFields()).fields,
  })
  const settingsQuery = useQuery({
    queryKey: ['config', 'settings'],
    queryFn: fetchPublicSettings,
  })

  const [customerId, setCustomerId] = useState(initialForm.customerId)
  const [name, setName] = useState(initialForm.name)
  const [stageId, setStageId] = useState(initialForm.stageId)
  const [startDate, setStartDate] = useState(initialForm.startDate)
  const [endDate, setEndDate] = useState(initialForm.endDate)
  const [billingType, setBillingType] = useState(initialForm.billingType)
  const [pattern, setPattern] = useState(initialForm.pattern)
  const [headcount, setHeadcount] = useState(initialForm.headcount)
  const [billRate, setBillRate] = useState(initialForm.billRate)
  const [driverCode, setDriverCode] = useState(initialForm.driverCode)
  const [sowRef, setSowRef] = useState(initialForm.sowRef)
  const [renewsId, setRenewsId] = useState(initialForm.renewsId)
  const [rationale, setRationale] = useState(initialForm.rationale)
  const [manualMonths, setManualMonths] = useState<Record<number, string>>(initialForm.manualMonths)
  const [monthOverrides, setMonthOverrides] = useState<Record<string, string>>(
    initialForm.monthOverrides,
  )
  const [manualMonthsEdited, setManualMonthsEdited] = useState(false)
  const [rampMonths, setRampMonths] = useState<Record<number, string>>(initialForm.rampMonths)
  const [rampMonthsEdited, setRampMonthsEdited] = useState(false)
  const [actualMonths, setActualMonths] = useState<Record<number, string>>(initialForm.actualMonths)
  const [actualMonthsEdited, setActualMonthsEdited] = useState(false)
  const [preview, setPreview] = useState<OpportunityPreviewResponse | null>(null)
  const [previewPending, setPreviewPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [confirmLost, setConfirmLost] = useState(false)
  const [detailTab, setDetailTab] = useState<'opportunity' | 'revenue' | 'history'>('opportunity')

  const auditQuery = useQuery({
    queryKey: ['opportunity-audit', opportunity?.id],
    queryFn: () => fetchOpportunityAudit(opportunity!.id),
    enabled: Boolean(opportunity?.id),
  })
  const [customValues, setCustomValues] = useState<Record<string, string>>(initialForm.customValues)
  const revenueDriverSignatureRef = useRef<string>('')

  useEffect(() => {
    const next = buildFormStateFromOpportunity(opportunity, year)
    setCustomerId(next.customerId)
    setName(next.name)
    setStageId(next.stageId)
    setStartDate(next.startDate)
    setEndDate(next.endDate)
    setBillingType(next.billingType)
    setPattern(next.pattern)
    setHeadcount(next.headcount)
    setBillRate(next.billRate)
    setDriverCode(next.driverCode)
    setSowRef(next.sowRef)
    setRenewsId(next.renewsId)
    setRationale(next.rationale)
    setManualMonths(next.manualMonths)
    setRampMonths(next.rampMonths)
    setActualMonths(next.actualMonths)
    setMonthOverrides(next.monthOverrides)
    setCustomValues(next.customValues)
    setManualMonthsEdited(false)
    setRampMonthsEdited(false)
    setActualMonthsEdited(false)
    setPreview(null)
    setError(null)
    setDirty(false)
    revenueDriverSignatureRef.current = ''
  }, [opportunity?.id, opportunity?.version, year, opportunity])

  const stages = useMemo(() => stagesQuery.data ?? [], [stagesQuery.data])
  const rules = rulesQuery.data
  const patterns = rules?.patterns ?? []
  const billingTypes = rules?.billing_types ?? []
  const method = patternMethod(patterns, pattern)

  const selectedStage = stages.find((s) => s.id === stageId)
  const probability = selectedStage?.probability ?? opportunity?.probability ?? 0

  const combinationAllowed = useMemo(() => {
    if (!rules) return true
    return rules.allowed_combinations.some(
      (c) => c.billing_type_code === billingType && c.revenue_pattern_code === pattern,
    )
  }, [rules, billingType, pattern])

  const rationaleThresholdPct = useMemo(() => {
    const raw = settingsQuery.data?.settings?.rationale_threshold_pct
    return typeof raw === 'number' && raw > 0 ? raw : 0.1
  }, [settingsQuery.data])

  const rationaleRequired = useMemo(() => {
    if (!isEdit) return true
    if (!preview) return false
    const sumYear = (months: OpportunityMonth[]) =>
      months.filter((m) => m.year === year).reduce((sum, m) => sum + Number(m.amount), 0)
    const prior = sumYear(preview.before_months)
    const next = sumYear(preview.after_months)
    if (prior === 0) return next > 0
    return Math.abs(next - prior) / prior > rationaleThresholdPct
  }, [isEdit, preview, year, rationaleThresholdPct])

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  const saveBlocked = rationaleRequired && !rationale.trim()

  useEffect(() => {
    onConfirmBlockedChange?.(saveBlocked)
  }, [saveBlocked, onConfirmBlockedChange])

  useEffect(() => {
    if (!stageId && stages.length > 0) {
      setStageId(stages[0]?.id ?? '')
    }
  }, [stages, stageId])

  useEffect(() => {
    if (!customerId && customersQuery.data?.length) {
      setCustomerId(customersQuery.data[0]?.id ?? '')
    }
  }, [customersQuery.data, customerId])

  const customFieldDefs = customFieldsQuery.data ?? []

  const buildCustomValuesPayload = () => {
    if (customFieldDefs.length === 0) return undefined
    const payload: Record<string, string | number | null> = {}
    for (const field of customFieldDefs) {
      const raw = customValues[field.code]
      if (raw === undefined || raw === '') {
        payload[field.code] = null
        continue
      }
      payload[field.code] = field.type === 'number' ? Number(raw) : raw
    }
    return payload
  }

  const buildPayload = () => ({
    customer_id: customerId,
    name,
    stage_id: stageId,
    start_date: startDate,
    end_date: endDate,
    billing_type_code: billingType,
    revenue_pattern_code: pattern,
    base_headcount: method === 'headcount' ? Number(headcount) || 0 : null,
    bill_rate: method === 'headcount' ? billRate : null,
    forecast_driver_code: driverCode || null,
    sow_reference: sowRef || null,
    renews_opportunity_id: renewsId.trim() || null,
    rationale: rationale.trim() || null,
    custom_values: buildCustomValuesPayload(),
    currency: opportunity?.currency ?? 'USD',
    // Only send month_inputs when the user edited the grid. Omitting them lets
    // the server calendar-shift stored ramp/manual inputs on start-date moves.
    month_inputs:
      method === 'manual_monthly' && manualMonthsEdited
        ? Object.entries(manualMonths)
            .filter(([, amount]) => amount !== '')
            .map(([month, amount]) => ({
              year,
              month: Number(month),
              amount: String(Math.round(Number(amount) || 0)),
            }))
        : pattern === RAMP_PATTERN && rampMonthsEdited
          ? Object.entries(rampMonths)
              .filter(([, hc]) => hc !== '')
              .map(([month, hc]) => ({
                year,
                month: Number(month),
                headcount: Number(hc) || 0,
              }))
          : undefined,
    month_actuals:
      finance && actualMonthsEdited
        ? Object.entries(actualMonths)
            .filter(([, amount]) => amount !== '')
            .map(([month, amount]) => ({
              year,
              month: Number(month),
              amount: String(Math.round(Number(amount) || 0)),
            }))
        : undefined,
    month_overrides:
      method === 'headcount'
        ? Object.entries(monthOverrides)
            .filter(([, amount]) => amount !== '')
            .map(([key, amount]) => {
              const [yearPart, monthPart] = key.split('-')
              return {
                year: Number(yearPart),
                month: Number(monthPart),
                amount: String(Math.round(Number(amount) || 0)),
              }
            })
            .filter((row) => Number.isFinite(row.year) && Number.isFinite(row.month))
        : undefined,
    opportunity_id: opportunity?.id ?? null,
    version: opportunity?.version ?? null,
  })

  const previewMutation = useMutation({
    mutationFn: () => previewOpportunity(buildPayload(), year),
    onMutate: () => setPreviewPending(true),
    onSuccess: (result) => {
      setPreview(result)
      setPreviewPending(false)
      setError(null)
    },
    onError: (caught) => {
      setPreviewPending(false)
      setError(caught instanceof ApiError ? caught.message : 'Preview failed.')
    },
  })

  useEffect(() => {
    const signature = [
      startDate,
      endDate,
      billingType,
      pattern,
      headcount,
      billRate,
      JSON.stringify(manualMonths),
      JSON.stringify(rampMonths),
      JSON.stringify(monthOverrides),
    ].join('|')
    if (revenueDriverSignatureRef.current && revenueDriverSignatureRef.current !== signature) {
      setRationale('')
    }
    revenueDriverSignatureRef.current = signature
  }, [
    startDate,
    endDate,
    billingType,
    pattern,
    headcount,
    billRate,
    manualMonths,
    rampMonths,
    monthOverrides,
  ])

  useEffect(() => {
    if (pattern !== RAMP_PATTERN || rampMonthsEdited) return
    if (Object.keys(rampMonths).length > 0) return
    const fromPreview = (preview?.after_months ?? []).filter(
      (m) => m.year === year && m.headcount != null,
    )
    if (!fromPreview.length) return
    const next: Record<number, string> = {}
    for (const row of fromPreview) {
      if (row.headcount != null) next[row.month] = String(row.headcount)
    }
    setRampMonths(next)
  }, [pattern, preview?.after_months, rampMonthsEdited, rampMonths, year])

  useEffect(() => {
    if (!startDate || !endDate || !customerId || !name || !combinationAllowed) return
    const timer = window.setTimeout(() => previewMutation.mutate(), 400)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced preview on field changes
  }, [
    customerId,
    name,
    stageId,
    startDate,
    endDate,
    billingType,
    pattern,
    headcount,
    billRate,
    driverCode,
    manualMonths,
    rampMonths,
    monthOverrides,
    year,
    combinationAllowed,
  ])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload()
      if (isEdit && opportunity) {
        return updateOpportunity(opportunity.id, {
          ...payload,
          expected_version: opportunity.version,
        })
      }
      return createOpportunity(payload)
    },
    onSuccess: () => {
      setDirty(false)
      onSaved?.()
    },
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(
          caught.code === 'rationale_required'
            ? 'Enter a rationale explaining this change before confirming.'
            : caught.message,
        )
      } else {
        setError('Could not save.')
      }
    },
  })

  const lostMutation = useMutation({
    mutationFn: () => {
      if (!opportunity) throw new Error('Missing opportunity')
      return markOpportunityLost(opportunity.id, opportunity.version)
    },
    onSuccess: () => onSaved?.(),
    onError: (caught) => {
      const message =
        caught instanceof ApiError ? caught.message : 'Could not mark opportunity as lost.'
      setError(message)
      toast.error(message)
    },
  })

  function markDirty() {
    setDirty(true)
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (rationaleRequired && !rationale.trim()) {
      setDetailTab('revenue')
      setError('A rationale is required for this change.')
      return
    }
    saveMutation.mutate()
  }

  function renderMonthChip(month: OpportunityMonth) {
    const locked = month.closed
    const overrideKey = `${month.year}-${month.month}`
    const editableComputed = method === 'headcount' && !locked && month.source !== 'manual'
    const storedWhole = String(Math.round(Number(month.amount) || 0))
    const amount = Number(monthOverrides[overrideKey] ?? storedWhole)
    const active = amount > 0
    const label = MONTH_LABELS[month.month - 1] ?? String(month.month)

    return (
      <div
        key={`${month.year}-${month.month}`}
        className={`month-chip${active ? ' month-chip--active' : ''}${locked ? ' month-chip--locked' : ''}`}
      >
        <span className="month-chip__label">{label}</span>
        {locked && finance ? (
          <input
            className="month-chip__input"
            type="number"
            min={0}
            step={1}
            aria-label={`${label} actual amount`}
            title="Closed month — finance actual"
            value={actualMonths[month.month] ?? storedWhole}
            onChange={(event) => {
              setActualMonthsEdited(true)
              setActualMonths((prev) => ({ ...prev, [month.month]: event.target.value }))
              markDirty()
            }}
          />
        ) : locked ? (
          <span className="month-chip__value" title="Closed by Finance">
            <Lock size={11} aria-hidden />
            <Money amount={month.amount} currency={month.currency} />
          </span>
        ) : editableComputed ? (
          <input
            className="month-chip__input"
            type="number"
            min={0}
            step={1}
            aria-label={`${label} amount`}
            value={monthOverrides[overrideKey] ?? storedWhole}
            onChange={(event) => {
              setMonthOverrides((prev) => ({ ...prev, [overrideKey]: event.target.value }))
              markDirty()
            }}
          />
        ) : (
          <span className="month-chip__value">
            <Money amount={month.amount} currency={month.currency} />
          </span>
        )}
      </div>
    )
  }

  const customerLabel =
    (customersQuery.data ?? []).find((c) => c.id === customerId)?.name ||
    opportunity?.customer_name ||
    '—'
  const ownerLabel = opportunity?.owner?.full_name?.trim() || '—'
  const updatedMeta = opportunity?.last_updated_at
    ? `Last updated ${formatDateTime(opportunity.last_updated_at)}${
        opportunity.owner?.full_name ? ` by ${opportunity.owner.full_name}` : ''
      }`
    : isEdit
      ? 'Existing line'
      : 'New line'
  const probabilityPct = Math.round(Number(probability) * 100)

  // Switch to revenue tab when save is blocked so the missing rationale is visible.
  useEffect(() => {
    if (saveBlocked && dirty) setDetailTab('revenue')
  }, [saveBlocked, dirty])

  const rationaleAttention = rationaleRequired && !rationale.trim()

  return (
    <form id={formId} className="opportunity-form opportunity-form--pane" onSubmit={handleSubmit}>
      {error && (
        <div className="opportunity-form__alerts">
          <div className="alert alert--error" role="alert">
            {error}
          </div>
        </div>
      )}

      <div className="opportunity-form__scroll">
        <header className="opportunity-form__hero">
          <div className="opportunity-form__hero-copy">
            <h3 className="opportunity-form__hero-title">
              {isEdit ? `Edit ${name || 'opportunity'}` : 'New opportunity'}
            </h3>
            <p className="opportunity-form__hero-meta">
              {customerLabel}
              <span className="opportunity-form__hero-sep" aria-hidden>
                |
              </span>
              {updatedMeta}
            </p>
          </div>
          {isEdit && (
            <button
              type="button"
              className="button button--secondary button--sm"
              disabled={lostMutation.isPending}
              onClick={() => setConfirmLost(true)}
            >
              Mark as lost
            </button>
          )}
        </header>

        <div className="opp-tabs">
          <div className="segmented opp-tabs__list" role="tablist" aria-label="Detail sections">
            <button
              type="button"
              role="tab"
              id="opp-tab-opportunity"
              aria-controls="opp-panel-opportunity"
              aria-selected={detailTab === 'opportunity'}
              tabIndex={detailTab === 'opportunity' ? 0 : -1}
              className={`segmented__item${
                detailTab === 'opportunity' ? ' segmented__item--active' : ''
              }`}
              onClick={() => setDetailTab('opportunity')}
            >
              Opportunity
            </button>
            <button
              type="button"
              role="tab"
              id="opp-tab-revenue"
              aria-controls="opp-panel-revenue"
              aria-selected={detailTab === 'revenue'}
              tabIndex={detailTab === 'revenue' ? 0 : -1}
              className={`segmented__item${
                detailTab === 'revenue' ? ' segmented__item--active' : ''
              }${rationaleAttention ? ' segmented__item--attention' : ''}`}
              onClick={() => setDetailTab('revenue')}
            >
              Revenue
            </button>
            {isEdit ? (
              <button
                type="button"
                role="tab"
                id="opp-tab-history"
                aria-controls="opp-panel-history"
                aria-selected={detailTab === 'history'}
                tabIndex={detailTab === 'history' ? 0 : -1}
                className={`segmented__item${
                  detailTab === 'history' ? ' segmented__item--active' : ''
                }`}
                onClick={() => setDetailTab('history')}
              >
                History
              </button>
            ) : null}
          </div>

          <section
            className="opp-card opp-tabs__panel"
            id="opp-panel-opportunity"
            role="tabpanel"
            aria-labelledby="opp-tab-opportunity"
            hidden={detailTab !== 'opportunity'}
          >
            <h4 className="opp-card__title">Opportunity</h4>
            <div className="opp-card__grid">
              <label className="field">
                <span className="field__label">Customer *</span>
                <select
                  className="field__input"
                  value={customerId}
                  required
                  onChange={(event) => {
                    setCustomerId(event.target.value)
                    markDirty()
                  }}
                >
                  {(customersQuery.data ?? []).map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="field__label">Project / opportunity name *</span>
                <input
                  className="field__input"
                  value={name}
                  required
                  onChange={(event) => {
                    setName(event.target.value)
                    markDirty()
                  }}
                />
              </label>

              <label className="field">
                <span className="field__label">Forecast driver *</span>
                <select
                  className="field__input"
                  value={driverCode}
                  onChange={(event) => {
                    setDriverCode(event.target.value)
                    markDirty()
                  }}
                >
                  <option value="">—</option>
                  {(driversQuery.data ?? []).map((driver) => (
                    <option key={driver.id} value={driver.code}>
                      {driver.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="field__label">Stage *</span>
                <select
                  className="field__input"
                  value={stageId}
                  onChange={(event) => {
                    setStageId(event.target.value)
                    markDirty()
                  }}
                >
                  {stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="field__label">Probability (from stage)</span>
                <input
                  className="field__input field__input--readonly"
                  value={`${probabilityPct}%`}
                  readOnly
                  tabIndex={-1}
                />
              </label>

              <label className="field">
                <span className="field__label">Owner</span>
                <input
                  className="field__input field__input--readonly"
                  value={ownerLabel}
                  readOnly
                  tabIndex={-1}
                />
              </label>

              <DateField
                label="Start date *"
                value={startDate}
                required
                compact
                onChange={(value) => {
                  setStartDate(value)
                  markDirty()
                }}
              />
              <DateField
                label="End date *"
                value={endDate}
                required
                compact
                onChange={(value) => {
                  setEndDate(value)
                  markDirty()
                }}
              />

              <label className="field">
                <span className="field__label">Renews opportunity ID</span>
                <input
                  className="field__input"
                  value={renewsId}
                  onChange={(event) => {
                    setRenewsId(event.target.value)
                    setDirty(true)
                  }}
                  placeholder="Optional prior-line UUID"
                />
              </label>
              <label className="field">
                <span className="field__label">SOW reference</span>
                <input
                  className="field__input"
                  value={sowRef}
                  onChange={(event) => {
                    setSowRef(event.target.value)
                    markDirty()
                  }}
                />
              </label>

              {customFieldDefs.length > 0 &&
                customFieldDefs.map((field) => (
                  <CustomFieldInput
                    key={field.code}
                    field={field}
                    value={customValues[field.code] ?? ''}
                    onChange={(value) => {
                      setCustomValues((prev) => ({ ...prev, [field.code]: value }))
                      markDirty()
                    }}
                  />
                ))}

              {opportunity?.legacy_billing_type_note && (
                <p className="callout callout--info callout--compact opp-card__full">
                  {opportunity.legacy_billing_type_note}
                </p>
              )}
            </div>
          </section>

          <section
            className="opp-card opp-tabs__panel"
            id="opp-panel-revenue"
            role="tabpanel"
            aria-labelledby="opp-tab-revenue"
            aria-live="polite"
            hidden={detailTab !== 'revenue'}
          >
            <div className="opp-card__title-row">
              <h4 className="opp-card__title">Revenue</h4>
              {previewPending && (
                <span className="opportunity-form__impact-status">Calculating…</span>
              )}
              {preview && (
                <div className="preview-totals preview-totals--inline">
                  <div className="preview-totals__item">
                    <span className="preview-totals__label">Weighted</span>
                    <Money
                      amount={preview.totals.after_weighted}
                      currency={preview.totals.currency}
                    />
                    <span className="preview-totals__delta">
                      Δ{' '}
                      <Money
                        amount={preview.totals.after_weighted - preview.totals.before_weighted}
                        currency={preview.totals.currency}
                      />
                    </span>
                  </div>
                  <div className="preview-totals__item">
                    <span className="preview-totals__label">TCV</span>
                    <Money amount={preview.totals.after_tcv} currency={preview.totals.currency} />
                  </div>
                </div>
              )}
            </div>

            <div className="opp-card__grid opp-card__grid--4">
              <label className="field">
                <span className="field__label">Billing type *</span>
                <select
                  className="field__input"
                  value={billingType}
                  onChange={(event) => {
                    setBillingType(event.target.value)
                    markDirty()
                  }}
                >
                  {billingTypes.map((bt) => (
                    <option key={bt.code} value={bt.code}>
                      {bt.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="field__label">Revenue pattern *</span>
                <select
                  className="field__input"
                  value={pattern}
                  onChange={(event) => {
                    setPattern(event.target.value)
                    markDirty()
                  }}
                >
                  {patterns.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>

              {method === 'headcount' ? (
                <>
                  <label className="field">
                    <span className="field__label">Base headcount</span>
                    <input
                      className="field__input"
                      type="number"
                      min={0}
                      value={headcount}
                      onChange={(event) => {
                        setHeadcount(event.target.value)
                        markDirty()
                      }}
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">Bill rate (USD / headcount / month)</span>
                    <input
                      className="field__input"
                      type="number"
                      min={0}
                      step="0.01"
                      value={billRate}
                      onChange={(event) => {
                        setBillRate(event.target.value)
                        markDirty()
                      }}
                    />
                  </label>
                </>
              ) : (
                <p className="field__hint opp-card__full">
                  Manual monthly amounts — enter values in the month profile below.
                </p>
              )}
            </div>

            {!combinationAllowed && (
              <p className="callout callout--warning callout--compact">
                This billing type and revenue pattern cannot be combined. Choose a valid pair.
              </p>
            )}

            {(preview?.wipe_overrides_warning ?? preview?.overrides_will_clear) && (
              <p className="callout callout--warning callout--compact">
                Driver changes will clear month overrides when saved.
                {preview?.wipe_overrides?.length ? (
                  <>
                    {' '}
                    Affected:{' '}
                    {preview.wipe_overrides
                      .map((m) => `${m.year}-${String(m.month).padStart(2, '0')}`)
                      .join(', ')}
                  </>
                ) : null}
              </p>
            )}

            {preview?.warnings.map((warning) => (
              <p
                key={warning.code}
                className={`callout callout--${warning.severity} callout--compact`}
              >
                {warning.message}
              </p>
            ))}

            {pattern === RAMP_PATTERN ? (
              <div className="month-chip-row" aria-label={`Ramp headcount schedule ${year}`}>
                {MONTH_LABELS.map((label, index) => {
                  const month = index + 1
                  const value = rampMonths[month] ?? ''
                  const active = Number(value) > 0
                  return (
                    <label
                      key={`ramp-${label}`}
                      className={`month-chip${active ? ' month-chip--active' : ''}`}
                    >
                      <span className="month-chip__label">{label}</span>
                      <input
                        className="month-chip__input"
                        type="number"
                        min={0}
                        step={1}
                        value={value}
                        aria-label={`${label} headcount`}
                        onChange={(event) => {
                          setRampMonthsEdited(true)
                          setRampMonths((prev) => ({ ...prev, [month]: event.target.value }))
                          markDirty()
                        }}
                      />
                    </label>
                  )
                })}
              </div>
            ) : null}

            {method === 'manual_monthly' ? (
              <div className="month-chip-row" aria-label={`Manual monthly amounts ${year}`}>
                {MONTH_LABELS.map((label, index) => {
                  const month = index + 1
                  const value = manualMonths[month] ?? ''
                  const active = Number(value) > 0
                  return (
                    <label
                      key={label}
                      className={`month-chip${active ? ' month-chip--active' : ''}`}
                    >
                      <span className="month-chip__label">{label}</span>
                      <input
                        className="month-chip__input"
                        type="number"
                        min={0}
                        step={1}
                        value={value}
                        onChange={(event) => {
                          setManualMonthsEdited(true)
                          setManualMonths((prev) => ({ ...prev, [month]: event.target.value }))
                          markDirty()
                        }}
                      />
                    </label>
                  )
                })}
              </div>
            ) : preview ? (
              <div className="month-chip-row" aria-label={`${year} monthly profile`}>
                {preview.after_months
                  .filter((m) => m.year === year)
                  .sort((a, b) => a.month - b.month)
                  .map(renderMonthChip)}
              </div>
            ) : (
              <p className="field__hint">Month profile appears when preview is ready.</p>
            )}

            <p className="opp-card__footnote">
              {pattern === RAMP_PATTERN
                ? `Ramp step-up: enter headcount by month for ${year}. Revenue uses headcount × bill rate (partial months prorated).`
                : method === 'headcount'
                  ? 'Revenue(m) = headcount × bill rate × partial-month factor. The start date counts as an active day.'
                  : `Manual monthly amounts for ${year}.`}
              {finance ? ' Closed months can be set to actual revenue below.' : ''}
              {startDate ? ` Term from ${formatDate(startDate)}` : ''}
              {endDate ? ` to ${formatDate(endDate)}.` : '.'}
            </p>

            <div className="opp-card__divider" />

            <h4 className="opp-card__title">Latest update / rationale</h4>
            <label className="field">
              <span
                className={`field__label${rationaleAttention ? ' field__label--attention' : ''}`}
              >
                Rationale
                {rationaleRequired
                  ? ` (required when the change moves ${year} revenue by more than ${
                      Math.round(rationaleThresholdPct * 1000) / 10
                    }%)`
                  : ' (optional)'}
              </span>
              <textarea
                className={`field__input opportunity-form__rationale${
                  rationaleAttention ? ' field__input--attention' : ''
                }`}
                rows={5}
                value={rationale}
                required={rationaleRequired}
                onChange={(event) => {
                  setRationale(event.target.value)
                  markDirty()
                }}
              />
            </label>
          </section>

          {isEdit ? (
            <section
              className="opp-card opp-tabs__panel"
              id="opp-panel-history"
              role="tabpanel"
              aria-labelledby="opp-tab-history"
              hidden={detailTab !== 'history'}
            >
              <h4 className="opp-card__title">Change history</h4>
              {auditQuery.isLoading ? (
                <p className="field__hint">Loading history…</p>
              ) : auditQuery.isError ? (
                <p className="field__hint">Could not load change history.</p>
              ) : (auditQuery.data?.length ?? 0) === 0 ? (
                <p className="field__hint">No recorded changes yet.</p>
              ) : (
                <ul className="opp-history-list">
                  {(auditQuery.data ?? []).map((event) => (
                    <li key={event.id} className="opp-history-list__item">
                      <div className="opp-history-list__head">
                        <span className="opp-history-list__action">{event.action}</span>
                        {event.ai_assisted ? (
                          <span className="badge badge--ai" title="AI-assisted change">
                            AI-assisted
                            {event.channel ? ` · ${event.channel}` : ''}
                          </span>
                        ) : null}
                      </div>
                      <p className="opp-history-list__meta">
                        {formatDateTime(event.occurred_at)}
                        {event.actor_email ? ` · ${event.actor_email}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirmLost}
        title="Mark opportunity lost?"
        confirmLabel="Mark lost"
        danger
        busy={lostMutation.isPending}
        onCancel={() => setConfirmLost(false)}
        onConfirm={() => {
          lostMutation.mutate(undefined, {
            onSettled: () => setConfirmLost(false),
          })
        }}
      >
        <p>
          This drops the line out of the weighted forecast. The row is kept for history and can be
          reviewed later.
        </p>
      </ConfirmDialog>
    </form>
  )
}

function CustomFieldInput({
  field,
  value,
  onChange,
}: {
  field: CustomFieldDefinition
  value: string
  onChange: (value: string) => void
}) {
  if (field.type === 'pick_list') {
    const options = field.options ?? []
    // Keep orphaned values selectable so saves are not blocked after an admin
    // removes an option that existing opportunities still hold.
    const optionsWithCurrent = value && !options.includes(value) ? [...options, value] : options
    return (
      <label className="field">
        <span className="field__label">{field.label}</span>
        <select className="field__input" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {optionsWithCurrent.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
              {!options.includes(opt) ? ' (removed)' : ''}
            </option>
          ))}
        </select>
      </label>
    )
  }
  const inputType = field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'
  return (
    <label className="field">
      <span className="field__label">{field.label}</span>
      <input
        className="field__input"
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}

export const OPPORTUNITY_FORM_ID = 'opportunity-form'
